import React, { useState, useCallback } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';
import { extractCVText, validateCVFile } from '../../services/cvParser';
import cvAnalysisService from '../../services/ai/core/CVAnalysisService';
import WizardSubmissionService from '../../services/wizardSubmissionService';
import { createInitialLeadFromCV } from '../../services/frappeLeadService';

/**
 * CV Upload Component for Wizard Step 6
 * Handles CV file upload, AI analysis, and storage
 */
const CVUpload = ({
  stepNumber = 7,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = "Upload Your CV",
  description = "Upload your CV for AI-powered analysis to enhance your immigration profile assessment and create a personalized consultation strategy.",
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();
  const [cvFile, setCvFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false); // Preview before upload
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [cvAnalysis, setCvAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [aiProvider, setAiProvider] = useState(null);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [frappeLeadStatus, setFrappeLeadStatus] = useState(null); // 'creating', 'created', 'failed'

  const renderReadableValue = (value, fallbackLabel = 'Details unavailable') => {
    if (value == null) return fallbackLabel;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value.toString();
    }
    if (Array.isArray(value)) {
      const filtered = value.filter((item) => item != null);
      if (filtered.length === 0) return fallbackLabel;
      return (
        <ul className="space-y-0.5">
          {filtered.map((item, idx) => (
            <li key={idx} className="list-disc list-inside">
              {renderReadableValue(item, fallbackLabel)}
            </li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'object') {
      const entries = Object.entries(value).filter(([, v]) => v != null);
      if (entries.length === 0) return fallbackLabel;
      return (
        <dl className="space-y-0.5">
          {entries.map(([key, v]) => (
            <div key={key}>
              <dt className="font-semibold capitalize text-xs text-orange-700">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </dt>
              <dd className="text-xs text-orange-800">
                {renderReadableValue(v, 'Not specified')}
              </dd>
            </div>
          ))}
        </dl>
      );
    }
    return fallbackLabel;
  };

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  }, []);

  // Validate file and set it - show preview instead of auto-uploading
  const validateAndSetFile = async (file) => {
    setError(null);

    try {
      validateCVFile(file);
    } catch (validationError) {
      setError(validationError.message || 'Invalid file. Please upload a PDF, DOCX, DOC, TXT, or RTF file under 10MB.');
      return;
    }

    setCvFile(file);
    setShowPreview(true); // Show preview instead of auto-upload
  };

  // Confirm upload from preview
  const confirmUpload = async () => {
    setShowPreview(false);
    await uploadAndAnalyzeCV(cvFile);
  };

  // Cancel upload in progress
  const cancelUpload = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setUploading(false);
    setAnalyzing(false);
    setUploadProgress(0);
    setUploadStartTime(null);
    setEstimatedTimeLeft(null);
    setError('Upload cancelled');
  };

  // Remove selected file
  const removeFile = () => {
    if (abortController) {
      abortController.abort();
    }
    setCvFile(null);
    setCvAnalysis(null);
    setShowPreview(false);
    setError(null);
    setUploadProgress(0);
    setAiProvider(null);
    setUploadStartTime(null);
    setEstimatedTimeLeft(null);
    setAbortController(null);
    // Clear Frappe lead state when CV is removed
    actions.setFrappeLead({
      leadId: null,
      status: 'pending',
      error: null
    });
    setFrappeLeadStatus(null);
  };

  // Calculate estimated time remaining
  const updateTimeEstimate = (progress) => {
    if (!uploadStartTime || progress <= 0) return;
    const elapsed = Date.now() - uploadStartTime;
    const totalEstimate = (elapsed / progress) * 100;
    const remaining = Math.max(0, totalEstimate - elapsed);
    setEstimatedTimeLeft(Math.ceil(remaining / 1000));
  };

  // Upload and analyze CV - now accepts file parameter
  const uploadAndAnalyzeCV = async (file) => {
    const fileToUpload = file || cvFile;
    if (!fileToUpload) return;

    try {
      setUploading(true);
      setAnalyzing(false);
      setError(null);
      setUploadProgress(0);
      setUploadStartTime(Date.now());
      setEstimatedTimeLeft(null);

      // Step 1: Upload CV (store as base64)
      setUploadProgress(15);
      updateTimeEstimate(15);
      const cvUploadInfo = await WizardSubmissionService.uploadCV(
        fileToUpload,
        state.personalInfo?.email || 'unknown'
      );

      // Step 2: Extract text from CV locally
      setUploadProgress(35);
      const cvText = await extractCVText(fileToUpload);
      if (!cvText || cvText.trim().length < 50) {
        throw new Error('Unable to extract meaningful text from the CV. Please upload a CV with selectable text (not scanned images).');
      }

      // Step 3: Build structured profile with AI (provider-agnostic)
      setUploadProgress(55);
      const structuredData = await cvAnalysisService.analyzeCV(cvText);
      const enrichedData = {
        ...structuredData,
        rawText: structuredData.rawText || cvText
      };

      // Track which provider was used
      setAiProvider(structuredData.provider || 'AI');

      // Step 4: Immigration-focused analysis with AI
      setUploading(false);
      setAnalyzing(true);
      setUploadProgress(75);
      const analysis = await cvAnalysisService.analyzeImmigrationProfile(state, enrichedData, cvText);
      setUploadProgress(100);

      // Step 5: Store analysis in context
      actions.setCVData({
        file: fileToUpload,
        url: cvUploadInfo?.publicUrl || null,
        storagePath: cvUploadInfo?.path || null,
        fileName: fileToUpload.name,
        fileType: fileToUpload.type,
        fileSize: fileToUpload.size,
        textLength: cvText.length,
        extractedData: enrichedData,
        aiAnalysis: analysis,
        aiProvider: analysis.provider || structuredData.provider || 'unknown',
        aiModel: analysis.model || structuredData.model || 'unknown',
        uploadedAt: new Date().toISOString()
      });

      setCvAnalysis(analysis);

      // Step 6: Create Frappe lead immediately with CV data
      setFrappeLeadStatus('creating');
      try {
        console.log('[CVUpload] Creating initial Frappe lead with CV data...');

        const cvDataWithUrls = {
          ...enrichedData,
          url: cvUploadInfo?.publicUrl || null,
          storagePath: cvUploadInfo?.path || null,
          fileName: fileToUpload.name,
          fileType: fileToUpload.type,
          fileSize: fileToUpload.size,
          aiAnalysis: analysis,
          uploadedAt: new Date().toISOString()
        };

        const frappeResult = await createInitialLeadFromCV(
          cvDataWithUrls,
          state,
          {
            maxRetries: 2,
            throwOnFail: false // Don't block wizard if Frappe fails
          }
        );

        if (frappeResult.success && frappeResult.leadId) {
          console.log('[CVUpload] Frappe lead created:', frappeResult.leadId);
          actions.setFrappeLead({
            leadId: frappeResult.leadId,
            status: 'created',
            createdAt: new Date().toISOString()
          });
          setFrappeLeadStatus('created');
        } else {
          console.warn('[CVUpload] Frappe lead creation failed:', frappeResult.error);
          // Store the failure but don't block the wizard
          actions.setFrappeLead({
            leadId: null,
            status: 'failed',
            error: frappeResult.error,
            createdAt: new Date().toISOString()
          });
          setFrappeLeadStatus('failed');
        }
      } catch (frappeError) {
        console.error('[CVUpload] Error creating Frappe lead:', frappeError);
        // Non-blocking: continue even if Frappe fails
        actions.setFrappeLead({
          leadId: null,
          status: 'failed',
          error: frappeError.message,
          createdAt: new Date().toISOString()
        });
        setFrappeLeadStatus('failed');
      }

    } catch (error) {
      console.error('Error uploading and analyzing CV:', error);
      setError(error.message || 'Failed to analyze CV with AI service. Please try again.');
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  // Handle next step
  const handleNext = useCallback(() => {
    if (!cvAnalysis) {
      setError('Please upload your CV before continuing.');
      return;
    }

    // Wait for Frappe lead creation to complete
    if (frappeLeadStatus === 'creating') {
      setError('Please wait while we register your profile...');
      return;
    }

    if (onNext) {
      onNext({
        cvUploaded: !!cvFile,
        cvAnalyzed: !!cvAnalysis,
        cvData: cvAnalysis,
        frappeLeadId: state.frappeLeadId,
        frappeLeadStatus: state.frappeLeadStatus
      });
    }
  }, [onNext, cvFile, cvAnalysis, frappeLeadStatus, state.frappeLeadId, state.frappeLeadStatus]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`cv-upload-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">{title}</h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">{description}</p>
      </div>

      {/* Upload Area */}
      <div className="card p-6 sm:p-8 mb-6">
        {!cvFile ? (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>

              <label htmlFor="cv-upload" className="cursor-pointer">
                <span className="text-lg font-medium text-gray-900">{t('wizard.cvUpload.clickToUpload', 'Click to upload your CV')}</span>
                <span className="text-sm text-gray-500 block mt-1">{t('wizard.cvUpload.orDragDrop', 'or drag and drop')}</span>
              </label>

              <input
                id="cv-upload"
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileSelect}
              />

              <p className="text-xs text-gray-500 mt-4">
                {t('wizard.cvUpload.fileTypes', 'PDF, DOC, DOCX, or TXT (max. 10MB)')}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* File Preview */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{cvFile.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(cvFile.size)}</p>
                </div>
              </div>

              <button
                onClick={removeFile}
                className="text-red-500 hover:text-red-700 transition-colors"
                disabled={uploading || analyzing}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Preview Confirmation - Before Upload */}
            {showPreview && !uploading && !analyzing && !cvAnalysis && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-900">{t('wizard.cvUpload.readyToUpload', 'Ready to upload?')}</p>
                    <p className="text-sm text-blue-700">
                      {t('wizard.cvUpload.aiAnalysisNotice', 'Your CV will be analyzed by AI to extract your profile information.')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={removeFile}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {t('wizard.common.cancel', 'Cancel')}
                    </button>
                    <button
                      onClick={confirmUpload}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      {t('wizard.cvUpload.uploadAndAnalyze', 'Upload & Analyze')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {(uploading || analyzing) && (
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">
                    {uploading ? t('wizard.cvUpload.uploading', 'Uploading CV...') : analyzing ? t('wizard.cvUpload.analyzing', 'Analyzing with AI{{provider}}...', { provider: aiProvider ? ` (${aiProvider})` : '' }) : t('wizard.cvUpload.processing', 'Processing...')}
                  </span>
                  <div className="flex items-center gap-3">
                    {estimatedTimeLeft !== null && estimatedTimeLeft > 0 && (
                      <span className="text-xs text-gray-500">
                        ~{estimatedTimeLeft}s remaining
                      </span>
                    )}
                    <span className="font-medium text-gray-900">{uploadProgress}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <button
                  onClick={cancelUpload}
                  className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel Upload
                </button>
              </div>
            )}

            {/* Success Message */}
            {cvAnalysis && !uploading && !analyzing && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">
                    CV uploaded and analyzed successfully! (Provider: {aiProvider || 'AI'})
                  </span>
                </div>

                {/* Frappe Lead Status */}
                {frappeLeadStatus === 'created' && state.frappeLeadId && (
                  <div className="flex items-center gap-2 text-blue-600 bg-blue-50 p-3 rounded-lg">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium">
                      Profile registered! Your lead ID is {state.frappeLeadId}
                    </span>
                  </div>
                )}

                {frappeLeadStatus === 'failed' && (
                  <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">
                      CV saved. Your profile will be created when you submit the wizard.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* CV Analysis Results */}
      {cvAnalysis && (
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            AI Analysis Complete
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Education Assessment */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Education Assessment</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li><span className="font-medium">Level:</span> {cvAnalysis.educationAssessment?.level || 'N/A'}</li>
                <li><span className="font-medium">Recognition:</span> {cvAnalysis.educationAssessment?.recognition || 'N/A'}</li>
                <li><span className="font-medium">Relevance:</span> {cvAnalysis.educationAssessment?.relevance || 'N/A'}</li>
              </ul>
            </div>

            {/* Experience Analysis */}
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Experience Analysis</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li><span className="font-medium">Total Experience:</span> {cvAnalysis.experienceAnalysis?.totalYears || 'N/A'} years</li>
                <li><span className="font-medium">Relevant Experience:</span> {cvAnalysis.experienceAnalysis?.relevantExperience || 'N/A'} years</li>
                <li><span className="font-medium">Industry Demand:</span> {cvAnalysis.experienceAnalysis?.industryDemand || 'N/A'}</li>
              </ul>
            </div>

            {/* Immigration Strengths */}
            {cvAnalysis.immigrationStrengths && cvAnalysis.immigrationStrengths.length > 0 && (
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-2">Immigration Strengths</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  {cvAnalysis.immigrationStrengths.slice(0, 3).map((strength, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <svg className="w-3 h-3 text-purple-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div className="text-sm text-purple-800">
                        {renderReadableValue(strength, 'Strength detail unavailable')}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommended Pathways */}
            {cvAnalysis.recommendedPathways && cvAnalysis.recommendedPathways.length > 0 && (
              <div className="bg-orange-50 rounded-lg p-4">
                <h4 className="font-medium text-orange-900 mb-2">Recommended Pathways</h4>
                <ul className="text-sm text-orange-800 space-y-1">
                  {cvAnalysis.recommendedPathways.slice(0, 3).map((pathway, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <svg className="w-3 h-3 text-orange-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                      </svg>
                      <div className="text-sm text-orange-800 space-y-0.5">
                        {typeof pathway === 'string' ? (
                          pathway
                        ) : (
                          <>
                            <p className="font-semibold">{pathway.pathway || pathway.label || 'Recommended Pathway'}</p>
                            {pathway.country && <p>Country: {pathway.country}</p>}
                            {pathway.score != null && <p>Score: {pathway.score}</p>}
                            {pathway.confidence && <p>Confidence: {pathway.confidence}</p>}
                            {pathway.reasoning && (
                              <div className="text-xs text-orange-700 leading-snug">
                                {renderReadableValue(pathway.reasoning, 'Reasoning unavailable')}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors order-2 sm:order-1"
        >
          Previous
        </button>

        <button
          onClick={handleNext}
          disabled={!cvAnalysis || uploading || analyzing}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default CVUpload;
